import { DefaultButton, DialogFooter, mergeStyleSets } from "@fluentui/react";
import { toast } from "components/FluentToast";
import { FormSpinner } from "components/FormSpinner";
import { Formik } from "formik";
import { useSyncClient } from "hooks/data/mutations/useSyncClient";
import { delay } from "library/delay";
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { setDialog, setFleetWithPremiums } from "../../../store/fleetForm";

const SynchronizeFleetClientFormDialog = () => {
  const { t } = useTranslation();
  const { mutate: onSyncClient, isLoading } = useSyncClient();
  const [timeoutError, setTimeoutError] = useState(false);

  const dispatch = useDispatch();
  const { fleetId } = useParams() as any;

  // Reset timeoutError when dialog is opened
  useEffect(() => {
    setTimeoutError(false);
  }, []);

  const close = () => {
    dispatch(setDialog({ type: "", isOpen: false }));
  };

  // No form fields anymore, keep Formik for submit/loader handling
  const initialValues = {};

  const classes = getClassNames();

  const onSubmit = () => {
    const payload = {}; // no comment field

    onSyncClient(
      {
        fleetId,
        body: {
          ...payload,
        },
      },
      {
        onSuccess: (res) => {
          toast.success(t("bfm.success"));
          close();
          dispatch(setFleetWithPremiums(res.data));
        },
        onError: (error: any) => {
          const status = error?.response?.status;
          const responseText = error?.response?.data;

          if (
            status === 500 &&
            typeof responseText === "string" &&
            responseText.includes("500 - The request timed out.")
          ) {
            setTimeoutError(true);
          } else {
            // Handle other errors with a generic message
            toast.error(t("bfm.error", "An error occurred"));
          }
        },
      }
    );
  };

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={async (_values, { setSubmitting }) => {
        onSubmit();
        await delay(400);
        setSubmitting(false);
      }}
    >
      {(props) => {
        const { handleSubmit } = props;

        return (
          <>
            <div className={classes.main}>
              <form onSubmit={handleSubmit}>
                <div className={classes.confirmText}>
                  {timeoutError
                    ? t(
                        "bfm.sync.inprogress",
                        "The operation is still in progress. Please wait a few minutes until it finishes."
                      )
                    : t(
                        "bfm.sync.confirm",
                        "Are you sure you want to synchronize?"
                      )}
                </div>

                <DialogFooter className={classes.footer}>
                  {timeoutError ? (
                    <DefaultButton
                      primary
                      text={t("bfm.ok", "OK")}
                      onClick={close}
                      disabled={isLoading}
                    />
                  ) : (
                    <DefaultButton
                      primary
                      text={t("bfm.sync", "Synchronize")}
                      type="submit"
                      iconProps={{
                        iconName: "Sync",
                      }}
                      disabled={isLoading}
                    />
                  )}
                </DialogFooter>

                {isLoading && <FormSpinner />}
              </form>
            </div>
          </>
        );
      }}
    </Formik>
  );
};

const getClassNames = () =>
  mergeStyleSets({
    main: {},
    confirmText: {
      width: "100%",
      marginTop: 20,
      fontSize: 14,
    },
    footer: {
      marginTop: 30,
    },
  });

export default SynchronizeFleetClientFormDialog;
